import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import HeaderV2 from '../../components/v2/HeaderV2';
import FooterV2 from '../../components/v2/FooterV2';
import ServiceDetailHeroV2 from '../../components/v2/ServiceDetailHeroV2';
import ServiceDetailSectionsV2 from '../../components/v2/ServiceDetailSectionsV2';
import AuditCTABannerV2 from '../../components/v2/AuditCTABannerV2';
import RelatedServicesV2 from '../../components/v2/RelatedServicesV2';
import {
  getServiceBySlug,
  getRelatedServices,
} from '../../services/v2ServicesRepository';
import useV2Language from '../../hooks/useV2Language';
import useDocumentLanguage from '../../hooks/useDocumentLanguage';
import '../../styles/v2/tokens.css';
import '../../styles/v2/landing.css';
import '../../styles/v2/services.css';

const NOT_FOUND_COPY = {
  en: {
    heading: "This service isn't published yet.",
    body: 'The link you followed may be out of date. Browse all published services from the index.',
    back: 'All services',
    doc_title: 'Service not found — illumi.dev',
  },
  es: {
    heading: 'Este servicio aún no está publicado.',
    body: 'El enlace que seguiste puede estar desactualizado. Explora todos los servicios publicados desde el índice.',
    back: 'Todos los servicios',
    doc_title: 'Servicio no encontrado — illumi.dev',
  },
};

const ServiceDetailV2 = () => {
  const { slug } = useParams();
  const { lang, setLang, buildHref } = useV2Language();
  useDocumentLanguage(lang);
  const [state, setState] = useState({
    status: 'loading',
    service: null,
    related: [],
    error: '',
  });
  const h1Ref = useRef(null);
  const notFoundHeadingRef = useRef(null);

  // Fetch service + related whenever slug or language changes
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    Promise.all([
      getServiceBySlug(slug, { lang }),
      getRelatedServices(slug, { lang, limit: 3 }),
    ])
      .then(([service, related]) => {
        if (!alive) return;
        if (!service) {
          setState({ status: 'not_found', service: null, related: [], error: '' });
        } else {
          setState({ status: 'success', service, related, error: '' });
        }
      })
      .catch((exc) => {
        if (!alive) return;
        setState({
          status: 'error',
          service: null,
          related: [],
          error: (exc && exc.message) || 'Could not load service.',
        });
      });
    return () => {
      alive = false;
    };
  }, [slug, lang]);

  // Document title + focus management
  useEffect(() => {
    if (state.status === 'success' && state.service) {
      document.title = state.service.seo.title || state.service.title;
      // Move focus to <h1> after route resolution
      if (h1Ref.current) h1Ref.current.focus();
    } else if (state.status === 'not_found') {
      document.title = (NOT_FOUND_COPY[lang] || NOT_FOUND_COPY.en).doc_title;
      if (notFoundHeadingRef.current) notFoundHeadingRef.current.focus();
    } else if (state.status === 'loading') {
      document.title = lang === 'es' ? 'Cargando servicio — illumi.dev' : 'Loading service — illumi.dev';
    } else if (state.status === 'error') {
      document.title = lang === 'es' ? 'Error de servicio — illumi.dev' : 'Service error — illumi.dev';
    }
  }, [state.status, state.service, lang]);

  return (
    <div className="illumi-v2" data-testid="illumi-v2-service-detail">
      <HeaderV2 language={lang} onLanguageChange={setLang} buildHref={buildHref} />

      <main>
        {state.status === 'loading' && (
          <div className="iv2-services-status iv2-container" role="status" data-testid="iv2-service-detail-loading">
            {lang === 'es' ? 'Cargando…' : 'Loading…'}
          </div>
        )}

        {state.status === 'error' && (
          <div className="iv2-services-status iv2-container" role="alert" data-testid="iv2-service-detail-error">
            {state.error}
          </div>
        )}

        {state.status === 'not_found' && (
          <section className="iv2-service-not-found" data-testid="iv2-service-detail-not-found">
            <div className="iv2-container">
              <h1
                ref={notFoundHeadingRef}
                tabIndex={-1}
                className="iv2-service-not-found-title"
                data-testid="iv2-service-detail-not-found-heading"
              >
                {(NOT_FOUND_COPY[lang] || NOT_FOUND_COPY.en).heading}
              </h1>
              <p className="iv2-service-not-found-body">
                {(NOT_FOUND_COPY[lang] || NOT_FOUND_COPY.en).body}
              </p>
              <Link
                to={`/v2/services?lang=${lang}`}
                className="iv2-btn iv2-btn-secondary"
                data-testid="iv2-service-detail-not-found-back"
              >
                {(NOT_FOUND_COPY[lang] || NOT_FOUND_COPY.en).back}
              </Link>
            </div>
          </section>
        )}

        {state.status === 'success' && state.service && (
          <>
            <ServiceDetailHeroV2 service={state.service} lang={lang} h1Ref={h1Ref} />
            <ServiceDetailSectionsV2 service={state.service} lang={lang} />
            <AuditCTABannerV2 lang={lang} ctaLabel={state.service.cta_label} />
            <RelatedServicesV2 services={state.related} lang={lang} />
          </>
        )}
      </main>
      <FooterV2 language={lang} buildHref={buildHref} />
    </div>
  );
};

export default ServiceDetailV2;
